import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'

interface Playlist {
  id: string
  name: string
  description?: string
  is_public: boolean
  created_at: string
  video_count?: number
  videos?: Array<{
    id: string
    title: string
    thumbnail_url: string
    duration: number
  }>
}

interface Props {
  currentUser: { id: string; username: string } | null
}

const PlaylistsScreen: React.FC<Props> = ({ currentUser }) => {
  const navigate = useNavigate()
  const { isDark } = useTheme()
  const [playlists, setPlaylists] = useState<Playlist[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null)

  const C = {
    bg: isDark ? '#080810' : '#f0f2f8',
    surface: isDark ? 'rgba(255,255,255,.03)' : '#fff',
    border: isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)',
    text: isDark ? '#fff' : '#0f172a',
    sub: isDark ? '#666' : '#94a3b8',
  }

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return }
    fetchPlaylists()
  }, [currentUser])

  const fetchPlaylists = async () => {
    if (!currentUser) return
    setLoading(true)
    try {
      const { data } = await supabase
        .from('playlists')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false })

      if (data) {
        const playlistsWithCount = await Promise.all(data.map(async p => {
          const { count } = await supabase
            .from('playlist_videos')
            .select('*', { count: 'exact', head: true })
            .eq('playlist_id', p.id)
          return { ...p, video_count: count || 0 }
        }))
        setPlaylists(playlistsWithCount)
      }
    } catch (e) {
      console.error('Error:', e)
    } finally {
      setLoading(false)
    }
  }

  const viewPlaylist = async (playlist: Playlist) => {
    const { data } = await supabase
      .from('playlist_videos')
      .select('*, video:videos(id, title, thumbnail_url, duration)')
      .eq('playlist_id', playlist.id)
      .order('position', { ascending: true })

    setSelectedPlaylist({
      ...playlist,
      videos: data?.map(pv => pv.video) || []
    })
  }

  const deletePlaylist = async (id: string) => {
    if (!window.confirm('Delete this playlist?')) return
    await supabase.from('playlists').delete().eq('id', id)
    setSelectedPlaylist(null)
    fetchPlaylists()
  }

  const removeFromPlaylist = async (playlistId: string, videoId: string) => {
    await supabase
      .from('playlist_videos')
      .delete()
      .eq('playlist_id', playlistId)
      .eq('video_id', videoId)
    viewPlaylist(selectedPlaylist!)
    fetchPlaylists()
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <button
            onClick={() => selectedPlaylist ? setSelectedPlaylist(null) : navigate(-1)}
            style={{ background: 'none', border: 'none', color: C.sub, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}
          >
            ← {selectedPlaylist ? 'Back to Playlists' : 'Back'}
          </button>
          <h1 style={{ color: C.text, fontSize: 24, fontWeight: 900, margin: 0, fontFamily: "'Syne',sans-serif" }}>
            📋 {selectedPlaylist ? selectedPlaylist.name : 'My Playlists'}
          </h1>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{
              width: 36, height: 36,
              border: '2px solid #e53935', borderTopColor: 'transparent',
              borderRadius: '50%', animation: 'spin .8s linear infinite',
              margin: '0 auto'
            }} />
          </div>
        ) : selectedPlaylist ? (
          /* Playlist Detail View */
          <div>
            {selectedPlaylist.description && (
              <p style={{ color: C.sub, fontSize: 14, marginBottom: 20 }}>
                {selectedPlaylist.description}
              </p>
            )}
            {selectedPlaylist.videos && selectedPlaylist.videos.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {selectedPlaylist.videos.map((video, idx) => (
                  <div
                    key={video.id}
                    style={{
                      display: 'flex', gap: 14, padding: 12,
                      borderRadius: 14, background: C.surface,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <span style={{
                      color: C.sub, fontSize: 14, fontWeight: 700,
                      minWidth: 24, display: 'flex', alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {idx + 1}
                    </span>
                    <Link to={`/video/${video.id}`} style={{ flexShrink: 0 }}>
                      <img
                        src={video.thumbnail_url}
                        alt=""
                        style={{
                          width: 120, height: 68,
                          borderRadius: 8, objectFit: 'cover'
                        }}
                      />
                    </Link>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Link to={`/video/${video.id}`} style={{ textDecoration: 'none' }}>
                        <h3 style={{
                          color: C.text, fontSize: 14, fontWeight: 600,
                          margin: 0, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {video.title}
                        </h3>
                      </Link>
                      <p style={{ color: C.sub, fontSize: 12, margin: '4px 0 0' }}>
                        {Math.floor(video.duration / 60)} min
                      </p>
                    </div>
                    <button
                      onClick={() => removeFromPlaylist(selectedPlaylist.id, video.id)}
                      style={{
                        background: 'none', border: 'none',
                        color: '#e53935', fontSize: 18, cursor: 'pointer',
                        padding: '0 8px'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <p style={{ color: C.sub }}>No videos in this playlist yet</p>
              </div>
            )}
            <button
              onClick={() => deletePlaylist(selectedPlaylist.id)}
              style={{
                marginTop: 20, padding: '10px 20px',
                borderRadius: 10, border: '1px solid rgba(229,57,53,.3)',
                background: 'transparent', color: '#e53935',
                fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Delete Playlist
            </button>
          </div>
        ) : playlists.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <p style={{ color: C.sub, fontSize: 15 }}>No playlists yet</p>
            <p style={{ color: C.sub, fontSize: 13 }}>
              Create playlists from video pages to organize your content
            </p>
          </div>
        ) : (
          /* Playlists Grid */
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: 16
          }}>
            {playlists.map(playlist => (
              <button
                key={playlist.id}
                onClick={() => viewPlaylist(playlist)}
                style={{
                  padding: 20, borderRadius: 14,
                  background: C.surface, border: `1px solid ${C.border}`,
                  cursor: 'pointer', textAlign: 'left',
                  transition: 'all .15s'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(229,57,53,.3)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = C.border
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
                <h3 style={{
                  color: C.text, fontSize: 16, fontWeight: 700,
                  margin: '0 0 4px'
                }}>
                  {playlist.name}
                </h3>
                <p style={{ color: C.sub, fontSize: 12, margin: 0 }}>
                  {playlist.video_count || 0} videos · {playlist.is_public ? '🌐 Public' : '🔒 Private'}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

export default PlaylistsScreen